import React, { useEffect, useState } from 'react';
import { API_BASE } from '../apiConfig';

function TemporaryApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [professionalSummary, setProfessionalSummary] = useState('');
  const [generalJobId, setGeneralJobId] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [personal, setPersonal] = useState({
    name: '',
    dateOfBirth: '',
    nationality: '',
    educationalDegree: '',
    university: '',
    postgraduateDegree: '',
    totalYearsExperienceOandG: ''
  });

  const [experiences, setExperiences] = useState([
    { employer: '', from: '', to: '', position: '', duties: '', current: false }
  ]);

  const [certificates, setCertificates] = useState([
    { iwcf: false, iadc: false, validTill: '' }
  ]);

  const [courses, setCourses] = useState([{ name: '', date: '' }]);
  const [trainings, setTrainings] = useState([{ name: '', date: '' }]);

  // Languages (searchable multi-select)
  const LANG_OPTIONS = [
    'Arabic', 'English', 'French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Russian', 'Chinese', 'Japanese', 'Korean', 'Hindi', 'Urdu', 'Bengali', 'Turkish', 'Persian', 'Malay', 'Indonesian', 'Thai', 'Vietnamese', 'Swahili', 'Dutch', 'Polish', 'Greek'
  ];
  const [languageQuery, setLanguageQuery] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const filteredLangs = LANG_OPTIONS.filter(l => l.toLowerCase().includes(languageQuery.toLowerCase()) && !selectedLanguages.includes(l)).slice(0, 6);

  const updatePersonal = (key, value) => setPersonal(prev => ({ ...prev, [key]: value }));

  const updateList = (list, setList, index, key, value) => {
    const copy = list.slice();
    copy[index] = { ...copy[index], [key]: value };
    setList(copy);
  };

  const addRow = (list, setList, empty) => setList(prev => [...prev, empty]);
  const removeRow = (list, setList, index) => setList(prev => prev.filter((_, i) => i !== index));

  // Load a valid job_id to satisfy FK (use a job titled General Application, otherwise first job)
  useEffect(() => {
    let mounted = true;
    setJobsLoading(true);
    fetch(`${API_BASE}/jobs`)
      .then(res => res.ok ? res.json() : [])
      .then(rows => {
        if (!mounted) return;
        const list = Array.isArray(rows) ? rows : [];
        let id = null;
        const general = list.find(j => (j.title || '').toLowerCase().includes('general application'));
        if (general) id = general.id;
        else if (list.length > 0) id = list[0].id;
        setGeneralJobId(id ?? null);
      })
      .catch(() => setGeneralJobId(null))
      .finally(() => setJobsLoading(false));
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    // Basic validation for key personal fields
    if (!personal.name || !personal.dateOfBirth || !personal.nationality) {
      setSubmitError('Please fill required personal fields.');
      return;
    }

    const applicantData = {
      type: 'temporary',
      personal,
      professionalSummary,
      experiences: experiences.filter(x => x.employer || x.position || x.duties),
      certificates: certificates.filter(x => x.iwcf || x.iadc || x.validTill),
      courses: courses.filter(x => x.name || x.date),
      trainings: trainings.filter(x => x.name || x.date),
      languages: selectedLanguages
    };

    try {
      setSubmitting(true);
      const jobIdToUse = 36;
      const res = await fetch(`${API_BASE}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobIdToUse, applicant_data: applicantData })
      });
      const data = await res.json().catch(async () => ({ status: 'error', message: await res.text() }));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || 'Failed to submit application');
      }
      setSubmitSuccess(true);
      // Reset form
      setPersonal({ name: '', dateOfBirth: '', nationality: '', educationalDegree: '', university: '', postgraduateDegree: '', totalYearsExperienceOandG: '' });
      setProfessionalSummary('');
      setExperiences([{ employer: '', from: '', to: '', position: '', duties: '', current: false }]);
      setCertificates([{ iwcf: false, iadc: false, validTill: '' }]);
      setCourses([{ name: '', date: '' }]);
      setTrainings([{ name: '', date: '' }]);
      setSelectedLanguages([]);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-5" style={{ maxWidth: 960 }}>
      <div className="mb-4 text-center">
        <h1 className="fw-bold" style={{ color: '#0c61c0' }}>Position Application</h1>
        <p className="text-muted mb-0">Please fill the following sections and submit your application.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {submitError && <div className="alert alert-danger">{submitError}</div>}
        {submitSuccess && <div className="alert alert-success">Your application has been submitted successfully.</div>}

        {/* Personal Data / Education */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3" style={{ color: '#1e293b' }}>Personal Data / Education</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={personal.name} onChange={e => updatePersonal('name', e.target.value)} required />
              </div>
              <div className="col-md-6">
                <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                <input type="date" className="form-control" value={personal.dateOfBirth} onChange={e => updatePersonal('dateOfBirth', e.target.value)} required />
              </div>
              <div className="col-md-6">
                <label className="form-label">Nationality <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={personal.nationality} onChange={e => updatePersonal('nationality', e.target.value)} required />
              </div>
              <div className="col-md-6">
                <label className="form-label">Educational Degree <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={personal.educationalDegree} onChange={e => updatePersonal('educationalDegree', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">University <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={personal.university} onChange={e => updatePersonal('university', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Postgraduate Degree (if any)</label>
                <input type="text" className="form-control" value={personal.postgraduateDegree} onChange={e => updatePersonal('postgraduateDegree', e.target.value)} />
                <div className="form-text">If none, write NA or leave it blank.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Total Years of Experience in O&G Industry <span className="text-danger">*</span></label>
                <input type="number" min="0" className="form-control" value={personal.totalYearsExperienceOandG} onChange={e => updatePersonal('totalYearsExperienceOandG', e.target.value)} />
              </div>
            </div>

      {/* Professional Summary */}
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body p-4">
          <h5 className="fw-bold mb-3" style={{ color: '#1e293b' }}>Professional Summary <span className="text-danger">*</span></h5>
          <textarea
            className="form-control"
            rows="5"
            placeholder="Provide a brief professional summary highlighting your expertise and achievements"
            value={professionalSummary}
            onChange={e => setProfessionalSummary(e.target.value)}
            required
          ></textarea>
        </div>
      </div>
          </div>
        </div>

        {/* Employment History */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0" style={{ color: '#1e293b' }}>Employment History</h5>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRow(experiences, setExperiences, { employer: '', from: '', to: '', position: '', duties: '', current: false })}>+ Add Employment</button>
            </div>
            {experiences.map((exp, idx) => (
              <div className="border rounded p-3 mb-3" key={idx}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Employer <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={exp.employer} onChange={e => updateList(experiences, setExperiences, idx, 'employer', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">From <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={exp.from} onChange={e => updateList(experiences, setExperiences, idx, 'from', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">To <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={exp.to} onChange={e => updateList(experiences, setExperiences, idx, 'to', e.target.value)} disabled={!!exp.current} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Position <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={exp.position} onChange={e => updateList(experiences, setExperiences, idx, 'position', e.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Duties <span className="text-danger">*</span></label>
                    <textarea className="form-control" rows="3" value={exp.duties} onChange={e => updateList(experiences, setExperiences, idx, 'duties', e.target.value)}></textarea>
                  </div>
                  {idx === 0 && (
                    <div className="col-12">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id={`current_${idx}`} checked={!!exp.current} onChange={e => updateList(experiences, setExperiences, idx, 'current', e.target.checked)} />
                        <label className="form-check-label" htmlFor={`current_${idx}`}>Current</label>
                      </div>
                    </div>
                  )}
                </div>
                {experiences.length > 1 && (
                  <div className="text-end mt-2">
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow(experiences, setExperiences, idx)}>Remove</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Certificates */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0" style={{ color: '#1e293b' }}>Certificates</h5>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRow(certificates, setCertificates, { iwcf: false, iadc: false, validTill: '' })}>+ Add Certificate</button>
            </div>
            {certificates.map((c, idx) => (
              <div className="border rounded p-3 mb-3" key={idx}>
                <div className="row g-3 align-items-end">
                  <div className="col-md-2">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id={`iwcf_${idx}`} checked={!!c.iwcf} onChange={e => updateList(certificates, setCertificates, idx, 'iwcf', e.target.checked)} />
                      <label className="form-check-label" htmlFor={`iwcf_${idx}`}>IWCF</label>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id={`iadc_${idx}`} checked={!!c.iadc} onChange={e => updateList(certificates, setCertificates, idx, 'iadc', e.target.checked)} />
                      <label className="form-check-label" htmlFor={`iadc_${idx}`}>IADC</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Valid till <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={c.validTill} onChange={e => updateList(certificates, setCertificates, idx, 'validTill', e.target.value)} />
                  </div>
                  {certificates.length > 1 && (
                    <div className="col-md-4 text-end">
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow(certificates, setCertificates, idx)}>Remove</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Courses */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0" style={{ color: '#1e293b' }}>Courses</h5>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRow(courses, setCourses, { name: '', date: '' })}>+ Add Course</button>
            </div>
            {courses.map((c, idx) => (
              <div className="row g-3 align-items-end mb-3" key={idx}>
                <div className="col-md-6">
                  <label className="form-label">Course Name <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={c.name} onChange={e => updateList(courses, setCourses, idx, 'name', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Date (optional)</label>
                  <input type="date" className="form-control" value={c.date || ''} onChange={e => updateList(courses, setCourses, idx, 'date', e.target.value)} />
                </div>
                {courses.length > 1 && (
                  <div className="col-md-2 text-end">
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow(courses, setCourses, idx)}>Remove</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trainings */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0" style={{ color: '#1e293b' }}>Trainings</h5>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRow(trainings, setTrainings, { name: '', date: '' })}>+ Add Training</button>
            </div>
            {trainings.map((t, idx) => (
              <div className="row g-3 align-items-end mb-3" key={idx}>
                <div className="col-md-6">
                  <label className="form-label">Training Name <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={t.name} onChange={e => updateList(trainings, setTrainings, idx, 'name', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Date (optional)</label>
                  <input type="date" className="form-control" value={t.date || ''} onChange={e => updateList(trainings, setTrainings, idx, 'date', e.target.value)} />
                </div>
                {trainings.length > 1 && (
                  <div className="col-md-2 text-end">
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow(trainings, setTrainings, idx)}>Remove</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3" style={{ color: '#1e293b' }}>Languages</h5>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Add Language</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type to search..."
                  value={languageQuery}
                  onChange={e => setLanguageQuery(e.target.value)}
                />
                {languageQuery && filteredLangs.length > 0 && (
                  <div className="border rounded mt-1 bg-white" style={{ maxHeight: 160, overflowY: 'auto' }}>
                    {filteredLangs.map(opt => (
                      <button type="button" key={opt} className="btn btn-sm w-100 text-start" onClick={() => { setSelectedLanguages(prev => [...prev, opt]); setLanguageQuery(''); }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-12">
                {selectedLanguages.length > 0 && (
                  <div className="mt-2">
                    {selectedLanguages.map(l => (
                      <span key={l} className="badge bg-primary me-2 mb-2">
                        {l}
                        <button type="button" className="btn btn-sm btn-link text-white ms-1 p-0" onClick={() => setSelectedLanguages(prev => prev.filter(x => x !== l))}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button type="submit" className="btn btn-primary px-5 py-2" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TemporaryApplication;


